import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3200';

function authHeaders(): Record<string, string> {
  try {
    const token =
      (JSON.parse(localStorage.getItem('urs-auth') ?? '{}')?.state ?? {})
        ?.token ?? '';
    return token
      ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
  } catch {
    return { 'Content-Type': 'application/json' };
  }
}

function fmtUGX(n: number): string {
  return `UGX ${Math.round(n).toLocaleString('en-UG')}`;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

// ── Interfaces ──────────────────────────────────────────────────────────────

interface Location {
  id: string;
  name: string;
}

interface RestaurantTable {
  id: string;
  tableNumber: string;
  capacity: number | null;
  section: string | null;
  isActive: boolean;
}

interface Reservation {
  id: string;
  guestName: string;
  guestPhone: string;
  partySize: number;
  date: string;
  time: string;
  tableId: string | null;
  status: 'booked' | 'confirmed' | 'seated' | 'completed' | 'no_show' | 'cancelled';
  specialRequests: string | null;
  depositUgx: number | null;
  channel: string;
  table?: { tableNumber: string; section: string | null } | null;
}

interface SummaryData {
  total: number;
  totals: {
    booked: number;
    confirmed: number;
    seated: number;
    completed: number;
    no_show: number;
    cancelled: number;
  };
  totalCovers: number;
  noShowCovers: number;
  noShowRate: number;
  byChannel: Record<string, number>;
}

type ReservationStatus = Reservation['status'];

// ── Helpers ──────────────────────────────────────────────────────────────────

function statusBadgeClass(status: ReservationStatus): string {
  const map: Record<ReservationStatus, string> = {
    booked: 'badge badge-amber',
    confirmed: 'badge badge-blue',
    seated: 'badge badge-green',
    completed: 'badge badge-gray',
    no_show: 'badge badge-red',
    cancelled: 'badge badge-gray',
  };
  return map[status];
}

function statusLabel(status: ReservationStatus): string {
  const map: Record<ReservationStatus, string> = {
    booked: 'Booked',
    confirmed: 'Confirmed',
    seated: 'Seated',
    completed: 'Completed',
    no_show: 'No-show',
    cancelled: 'Cancelled',
  };
  return map[status];
}

function channelLabel(channel: string): string {
  const map: Record<string, string> = {
    phone: 'Phone',
    walk_in: 'Walk-in',
    online: 'Online',
    whatsapp: 'WhatsApp',
  };
  return map[channel] ?? channel;
}

// ── New Reservation Modal ─────────────────────────────────────────────────────

interface NewReservationModalProps {
  locationId: string;
  onClose: () => void;
  onSave: (data: NewReservationPayload) => void;
  isSaving: boolean;
  error: string | null;
}

interface NewReservationPayload {
  locationId: string;
  guestName: string;
  guestPhone: string;
  partySize: number;
  date: string;
  time: string;
  tableId?: string;
  specialRequests?: string;
  depositUgx?: number;
  channel: string;
}

function NewReservationModal({ locationId, onClose, onSave, isSaving, error }: NewReservationModalProps) {
  const [guestName, setGuestName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [partySize, setPartySize] = useState(2);
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState('19:00');
  const [tableId, setTableId] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [depositUgx, setDepositUgx] = useState('');
  const [channel, setChannel] = useState<string>('phone');

  const { data: availabilityData, isLoading: avLoading } = useQuery<{ available: RestaurantTable[] }>({
    queryKey: ['reservation-availability', locationId, date, time, partySize],
    queryFn: async () => {
      const params = new URLSearchParams({ locationId, date, time, partySize: String(partySize) });
      const res = await fetch(`${API_BASE}/v1/reservations/availability?${params}`, { headers: authHeaders() });
      const j = await res.json() as { data?: { available: RestaurantTable[] } };
      return j.data ?? { available: [] };
    },
    enabled: Boolean(locationId && date && time && partySize > 0),
  });

  const availableTables = availabilityData?.available ?? [];

  const canSave = guestName.trim() !== '' && guestPhone.trim() !== '' && partySize > 0 && date !== '' && time !== '';

  function handleSubmit() {
    if (!canSave) return;
    const payload: NewReservationPayload = {
      locationId,
      guestName: guestName.trim(),
      guestPhone: guestPhone.trim(),
      partySize,
      date,
      time,
      channel,
      ...(tableId ? { tableId } : {}),
      ...(specialRequests.trim() ? { specialRequests: specialRequests.trim() } : {}),
      ...(depositUgx !== '' ? { depositUgx: parseInt(depositUgx) || 0 } : {}),
    };
    onSave(payload);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 className="modal-title">New Reservation</h2>
          <button className="modal-close" onClick={onClose} disabled={isSaving}>&times;</button>
        </div>
        <div className="modal-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, overflowY: 'auto', maxHeight: '60vh' }}>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Guest Name *</label>
            <input className="input" value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="e.g. Sarah Nakato" />
          </div>
          <div className="field">
            <label className="field-label">Phone *</label>
            <input className="input" value={guestPhone} onChange={e => setGuestPhone(e.target.value)} placeholder="+256 7XX XXX XXX" />
          </div>
          <div className="field">
            <label className="field-label">Party Size *</label>
            <input className="input" type="number" min={1} max={50} value={partySize} onChange={e => setPartySize(parseInt(e.target.value) || 1)} />
          </div>
          <div className="field">
            <label className="field-label">Date *</label>
            <input className="input" type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="field">
            <label className="field-label">Time *</label>
            <input className="input" type="time" value={time} onChange={e => setTime(e.target.value)} />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">
              Table {avLoading
                ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(checking availability…)</span>
                : <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>({availableTables.length} available)</span>}
            </label>
            <select className="input" value={tableId} onChange={e => setTableId(e.target.value)}>
              <option value="">— Auto-assign —</option>
              {availableTables.map(t => (
                <option key={t.id} value={t.id}>
                  Table {t.tableNumber}{t.section ? ` (${t.section})` : ''}{t.capacity ? ` · cap ${t.capacity}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field-label">Channel</label>
            <select className="input" value={channel} onChange={e => setChannel(e.target.value)}>
              <option value="phone">Phone</option>
              <option value="walk_in">Walk-in</option>
              <option value="online">Online</option>
              <option value="whatsapp">WhatsApp</option>
            </select>
          </div>
          <div className="field">
            <label className="field-label">Deposit (UGX)</label>
            <input className="input" type="number" min={0} value={depositUgx} onChange={e => setDepositUgx(e.target.value)} placeholder="Optional" />
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label className="field-label">Special Requests</label>
            <textarea
              className="input"
              value={specialRequests}
              onChange={e => setSpecialRequests(e.target.value)}
              rows={2}
              placeholder="Dietary requirements, birthday cake, etc."
              style={{ resize: 'none' }}
            />
          </div>
          {error && (
            <div
              style={{
                gridColumn: '1 / -1',
                background: 'var(--red-bg)',
                border: '1px solid var(--red)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 13,
                color: 'var(--red)',
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={!canSave || isSaving}>
            {isSaving ? 'Saving…' : 'Create Reservation'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────

interface DetailModalProps {
  reservation: Reservation;
  onClose: () => void;
  onTransition: (id: string, status: ReservationStatus) => void;
  isTransitioning: boolean;
}

function DetailModal({ reservation: r, onClose, onTransition, isTransitioning }: DetailModalProps) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h2 className="modal-title">{r.guestName}</h2>
            <span className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</span>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              { label: 'Phone', value: r.guestPhone },
              { label: 'Party Size', value: `${r.partySize} guests` },
              {
                label: 'Date & Time',
                value: `${new Date(r.date).toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' })} at ${r.time}`,
              },
              {
                label: 'Table',
                value: r.table ? `Table ${r.table.tableNumber}${r.table.section ? ` · ${r.table.section}` : ''}` : '—',
              },
              { label: 'Channel', value: channelLabel(r.channel) },
              { label: 'Deposit', value: r.depositUgx != null ? fmtUGX(r.depositUgx) : '—' },
            ].map(item => (
              <div key={item.label}>
                <p style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {item.label}
                </p>
                <p style={{ fontSize: 13, color: 'var(--text)', marginTop: 3 }}>{item.value}</p>
              </div>
            ))}
          </div>
          {r.specialRequests && (
            <div style={{ marginTop: 14 }}>
              <p style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Special Requests
              </p>
              <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4, background: 'var(--amber-bg)', borderRadius: 8, padding: '8px 12px' }}>
                {r.specialRequests}
              </p>
            </div>
          )}
        </div>
        <div className="modal-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          <ActionButtons reservation={r} onTransition={onTransition} isTransitioning={isTransitioning} />
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Action Buttons ────────────────────────────────────────────────────────────

function ActionButtons({
  reservation,
  onTransition,
  isTransitioning,
}: {
  reservation: Reservation;
  onTransition: (id: string, status: ReservationStatus) => void;
  isTransitioning: boolean;
}) {
  const { status, id } = reservation;

  const btn = (label: string, nextStatus: ReservationStatus, extraStyle?: React.CSSProperties) => (
    <button
      key={nextStatus}
      disabled={isTransitioning}
      onClick={() => onTransition(id, nextStatus)}
      className="btn btn-ghost btn-sm"
      style={{ fontSize: 12, ...extraStyle }}
    >
      {label}
    </button>
  );

  const actions: React.ReactElement[] = [];

  if (status === 'booked') {
    actions.push(btn('Confirm', 'confirmed', { color: 'var(--accent)' }));
    actions.push(btn('Cancel', 'cancelled', { color: 'var(--red)' }));
  }
  if (status === 'confirmed') {
    actions.push(btn('Seat', 'seated', { color: 'var(--green)' }));
    actions.push(btn('No-show', 'no_show', { color: 'var(--amber)' }));
    actions.push(btn('Cancel', 'cancelled', { color: 'var(--red)' }));
  }
  if (status === 'seated') {
    actions.push(btn('Complete', 'completed'));
    actions.push(btn('No-show', 'no_show', { color: 'var(--amber)' }));
  }

  return <>{actions}</>;
}

// ── Bookings Tab ──────────────────────────────────────────────────────────────

const STATUS_FILTERS: Array<{ value: ReservationStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'booked', label: 'Booked' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'seated', label: 'Seated' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No-show' },
  { value: 'cancelled', label: 'Cancelled' },
];

interface BookingsTabProps {
  locationId: string;
}

function BookingsTab({ locationId }: BookingsTabProps) {
  const qc = useQueryClient();
  const [date, setDate] = useState(todayISO());
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | 'all'>('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [newModalError, setNewModalError] = useState<string | null>(null);
  const [detailReservation, setDetailReservation] = useState<Reservation | null>(null);

  const { data: reservations = [], isLoading } = useQuery<Reservation[]>({
    queryKey: ['reservations', locationId, date, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (locationId) params.set('locationId', locationId);
      if (date) params.set('date', date);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      const res = await fetch(`${API_BASE}/v1/reservations?${params}`, { headers: authHeaders() });
      const j = await res.json() as { data?: Reservation[] };
      if (!res.ok) {
        const errJ = j as { error?: { message?: string } };
        throw new Error(errJ.error?.message ?? 'Failed to load reservations');
      }
      return j.data ?? [];
    },
    enabled: Boolean(locationId),
  });

  const createReservation = useMutation({
    mutationFn: async (data: NewReservationPayload) => {
      const res = await fetch(`${API_BASE}/v1/reservations`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(data),
      });
      const j = await res.json() as { data?: Reservation; error?: { message?: string } };
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed to create reservation');
      return j.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reservations'] });
      setShowNewModal(false);
      setNewModalError(null);
    },
    onError: (err: Error) => {
      setNewModalError(err.message);
    },
  });

  const transitionMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ReservationStatus }) => {
      const res = await fetch(`${API_BASE}/v1/reservations/${id}/transition`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      const j = await res.json() as { data?: Reservation; error?: { message?: string } };
      if (!res.ok) throw new Error(j.error?.message ?? 'Transition failed');
      return j.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reservations'] });
      setDetailReservation(null);
    },
  });

  function handleTransition(id: string, status: ReservationStatus) {
    transitionMutation.mutate({ id, status });
  }

  return (
    <>
      {/* Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <input
          className="input"
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{ width: 160 }}
        />
        <button
          className="btn btn-primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => { setNewModalError(null); setShowNewModal(true); }}
        >
          + New Reservation
        </button>
      </div>

      {/* Status filter tabs */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 16 }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            style={{
              padding: '5px 12px',
              borderRadius: 7,
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              background: statusFilter === f.value ? 'var(--surface)' : 'transparent',
              color: statusFilter === f.value ? 'var(--text)' : 'var(--text-3)',
              boxShadow: statusFilter === f.value ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {f.label}
            {f.value !== 'all' && (
              <span style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {reservations.filter(r => r.status === f.value).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} style={{ height: 64, background: 'var(--surface2)', borderRadius: 8, animation: 'pulse 1.5s infinite' }} />
            ))}
          </div>
        ) : reservations.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No reservations for this date.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Guest</th>
                  <th style={{ textAlign: 'center' }}>Party</th>
                  <th>Table</th>
                  <th>Channel</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {reservations.map(r => (
                  <tr
                    key={r.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setDetailReservation(r)}
                  >
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--text)', fontWeight: 500 }}>{r.time}</td>
                    <td>
                      <p style={{ fontWeight: 500, color: 'var(--text)' }}>{r.guestName}</p>
                      <p style={{ fontSize: 11, color: 'var(--text-3)' }}>{r.guestPhone}</p>
                    </td>
                    <td style={{ textAlign: 'center', color: 'var(--text-2)' }}>{r.partySize}</td>
                    <td style={{ color: 'var(--text-2)' }}>
                      {r.table ? `T${r.table.tableNumber}${r.table.section ? ` · ${r.table.section}` : ''}` : '—'}
                    </td>
                    <td style={{ color: 'var(--text-3)' }}>{channelLabel(r.channel)}</td>
                    <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                      <span className={statusBadgeClass(r.status)}>{statusLabel(r.status)}</span>
                    </td>
                    <td style={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                        <ActionButtons
                          reservation={r}
                          onTransition={handleTransition}
                          isTransitioning={transitionMutation.isPending}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showNewModal && (
        <NewReservationModal
          locationId={locationId}
          onClose={() => { setShowNewModal(false); setNewModalError(null); }}
          onSave={data => createReservation.mutate(data)}
          isSaving={createReservation.isPending}
          error={newModalError}
        />
      )}
      {detailReservation && (
        <DetailModal
          reservation={detailReservation}
          onClose={() => setDetailReservation(null)}
          onTransition={handleTransition}
          isTransitioning={transitionMutation.isPending}
        />
      )}
    </>
  );
}

// ── Summary Tab ───────────────────────────────────────────────────────────────

interface SummaryTabProps {
  locationId: string;
}

function SummaryTab({ locationId }: SummaryTabProps) {
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());

  const { data: summary, isLoading } = useQuery<SummaryData | undefined>({
    queryKey: ['reservations-summary', locationId, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams({ locationId, dateFrom, dateTo });
      const res = await fetch(`${API_BASE}/v1/reservations/reports/summary?${params}`, {
        headers: authHeaders(),
      });
      const j = await res.json() as { data?: SummaryData; error?: { message?: string } };
      if (!res.ok) throw new Error(j.error?.message ?? 'Failed');
      return j.data;
    },
    enabled: Boolean(locationId && dateFrom && dateTo),
  });

  const statusRows: Array<{ key: keyof SummaryData['totals']; label: string; cls: string }> = [
    { key: 'booked', label: 'Booked', cls: 'badge badge-amber' },
    { key: 'confirmed', label: 'Confirmed', cls: 'badge badge-blue' },
    { key: 'seated', label: 'Seated', cls: 'badge badge-green' },
    { key: 'completed', label: 'Completed', cls: 'badge badge-gray' },
    { key: 'no_show', label: 'No-show', cls: 'badge badge-red' },
    { key: 'cancelled', label: 'Cancelled', cls: 'badge badge-gray' },
  ];

  const channelRows = summary
    ? Object.entries(summary.byChannel).map(([ch, count]) => ({ channel: ch, count }))
    : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Date range */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-3)' }}>From</label>
          <input className="input" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ width: 160 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-3)' }}>To</label>
          <input className="input" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ width: 160 }} />
        </div>
      </div>

      {/* KPI Cards */}
      {isLoading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 96, background: 'var(--surface2)', borderRadius: 12, animation: 'pulse 1.5s infinite' }} />
          ))}
        </div>
      ) : summary ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16 }}>
          <div className="stat-card">
            <p className="stat-label">Total Reservations</p>
            <p className="stat-value">{summary.total}</p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Total Covers</p>
            <p className="stat-value">{summary.totalCovers}</p>
          </div>
          <div className={`stat-card${summary.noShowRate > 0.1 ? ' red' : ''}`}>
            <p className="stat-label">No-show Rate</p>
            <p className="stat-value" style={{ color: summary.noShowRate > 0.1 ? 'var(--red)' : undefined }}>
              {(summary.noShowRate * 100).toFixed(1)}%
            </p>
          </div>
          <div className="stat-card">
            <p className="stat-label">Completed</p>
            <p className="stat-value">{summary.totals.completed}</p>
          </div>
        </div>
      ) : (
        <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No data available.</p>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Status breakdown */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>By Status</h3>
          </div>
          {isLoading ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={{ height: 32, background: 'var(--surface2)', borderRadius: 6, animation: 'pulse 1.5s infinite' }} />
              ))}
            </div>
          ) : summary ? (
            <div className="table-wrap">
              <table>
                <tbody>
                  {statusRows.map(row => (
                    <tr key={row.key}>
                      <td><span className={row.cls}>{row.label}</span></td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>{summary.totals[row.key]}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-3)', fontSize: 12, width: 48 }}>
                        {summary.total > 0
                          ? `${((summary.totals[row.key] / summary.total) * 100).toFixed(0)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {/* Channel breakdown */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>By Channel</h3>
          </div>
          {isLoading ? (
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} style={{ height: 32, background: 'var(--surface2)', borderRadius: 6, animation: 'pulse 1.5s infinite' }} />
              ))}
            </div>
          ) : channelRows.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center' }}>
              <p style={{ fontSize: 13, color: 'var(--text-3)' }}>No channel data for this period.</p>
            </div>
          ) : (
            <div className="table-wrap">
              <table>
                <tbody>
                  {channelRows.map(row => (
                    <tr key={row.channel}>
                      <td style={{ fontWeight: 500, color: 'var(--text-2)' }}>{channelLabel(row.channel)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>{row.count}</td>
                      <td style={{ textAlign: 'right', color: 'var(--text-3)', fontSize: 12, width: 48 }}>
                        {summary && summary.total > 0
                          ? `${((row.count / summary.total) * 100).toFixed(0)}%`
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ReservationsPage() {
  const [activeTab, setActiveTab] = useState<'bookings' | 'summary'>('bookings');
  const [locationId, setLocationId] = useState('');

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/v1/locations`, { headers: authHeaders() });
      const j = await res.json() as { data?: Location[] };
      return j.data ?? [];
    },
  });

  useEffect(() => {
    if (locations.length > 0 && locationId === '') {
      setLocationId(locations[0]!.id);
    }
  }, [locations, locationId]);

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--text-3)', fontSize: 13 }}>Manage table bookings and guest reservations</p>
        </div>
        {locations.length > 1 && (
          <select className="input" style={{ width: 200 }} value={locationId} onChange={e => setLocationId(e.target.value)}>
            {locations.map(l => (
              <option key={l.id} value={l.id}>{l.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, background: 'var(--surface2)', borderRadius: 10, padding: 4, width: 'fit-content', marginBottom: 20 }}>
        {(['bookings', 'summary'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '6px 16px',
              borderRadius: 7,
              border: 'none',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              background: activeTab === tab ? 'var(--surface)' : 'transparent',
              color: activeTab === tab ? 'var(--text)' : 'var(--text-3)',
              boxShadow: activeTab === tab ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s',
            }}
          >
            {tab === 'bookings' ? 'Bookings' : 'Summary'}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {locationId === '' ? (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--text-3)' }}>Loading locations…</p>
        </div>
      ) : activeTab === 'bookings' ? (
        <BookingsTab locationId={locationId} />
      ) : (
        <SummaryTab locationId={locationId} />
      )}
    </div>
  );
}
